/**
 * SSO Service
 *
 * Handles Single Sign-On authentication flow for KeyLearning.
 * Supports multiple providers (Azure AD, Google) per institution.
 * Provides methods to:
 * - List institutions with SSO enabled (with available providers)
 * - Initiate SSO login flow for any provider
 * - Handle OAuth callback and token extraction
 */

import axios from '@/utils/api'

export default {
  /**
   * Get list of institutions with any SSO provider enabled
   * @returns {Promise<Array>} List of institutions with providers array
   */
  async getInstitutionsWithSSO() {
    try {
      const response = await axios.get('/authentication/sso/institutions/')
      return response.data.institutions || []
    } catch (error) {
      console.error('Error fetching SSO institutions:', error)
      throw error
    }
  },

  /**
   * Initiate SSO login flow for a specific provider
   * Redirects user to the identity provider's login page
   * @param {number} institutionId - Institution ID
   * @param {string} provider - SSO provider ('azure' or 'google')
   */
  async initiateLogin(institutionId, provider) {
    try {
      const response = await axios.get('/authentication/sso/login/', {
        params: {
          institution_id: institutionId,
          provider: provider,
          frontend_origin: window.location.origin
        }
      })

      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url
      } else {
        throw new Error('No authorization URL received from server')
      }
    } catch (error) {
      console.error(`Error initiating ${provider} login:`, error)
      throw error
    }
  },

  /**
   * Extract SSO result from URL parameters
   * Backend redirects here after successful authentication.
   * SEC-3 fase 2: los tokens ya NO viajan en la URL — el backend deja el
   * refresh token en una cookie HttpOnly y el caller debe despachar
   * 'restoreSession' (silent refresh) para establecer la sesión.
   * Supports both new (sso_login) and legacy (azure_login) parameter names
   * @returns {Object|null} { success: true } | { success: false, error } | null
   */
  extractTokensFromUrl() {
    const urlParams = new URLSearchParams(window.location.search)
    const ssoLogin = urlParams.get('sso_login') || urlParams.get('azure_login')

    if (!ssoLogin) {
      return null
    }

    if (ssoLogin === 'success') {
      return { success: true }
    }

    if (ssoLogin === 'error') {
      const error = urlParams.get('error')
      return {
        success: false,
        error: error || 'Unknown error during SSO authentication'
      }
    }

    return null
  },

  /**
   * Clear SSO parameters from URL
   * Removes sso_login, azure_login, access, refresh, and error params
   */
  clearUrlParams() {
    const url = new URL(window.location.href)
    ;['sso_login', 'azure_login', 'access', 'refresh', 'error'].forEach(p => url.searchParams.delete(p))
    window.history.replaceState({}, document.title, url.pathname + url.search)
  }
}
